import type { ChangeEventHandler, ReactNode } from 'react'
import { useCallback, useEffect, useState } from 'react'
import { defineMessages, useIntl } from 'react-intl'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import PhoneInput from 'components/Common/Form/Input/PhoneInput'
import Group from 'components/utils/Group'
import TextArea from 'components/Common/Form/Input/TextArea'
import TextInput from 'components/Common/Form/Input/TextInput'
import HiddenInput from 'components/Common/Form/Input/HiddenInput'
import LineCalendarIcon from 'LuxKit/Icons/line/LineCalendarIcon'
import AgentCallbackRequestDateTimeInput from './RequestAgentCallbackDateTimeInput'
import { parseSelectedNumberOfTravellersToFormText, parseSelectedTravelDatesToFormText } from './RequestAgentCallbackUtils'

export type RequestAgentCallbackRequester =
  | { type: 'authenticated', account: App.AuthAccount }
  | { type: 'guest' }

interface Props {
  type: App.GenesysAgentCallbackType
  requester: RequestAgentCallbackRequester
  messagePlaceholder?: string
  filters?: App.OfferListFilters
  location?: string
  campaign?: string
  prefillFirstName?: string
  prefillLastName?: string
  prefillEmail?: string
  prefillPhone?: string
  prefillPhonePrefix?: string
  emailReadOnly?: boolean
  children?: ReactNode
}

const MAX_LENGTH = 100
const MAX_LENGTH_COMMENTS = 500
const CALLBACK_TYPES_WITH_INTRO = ['FLIGHTS', 'LESA', 'SALES', 'SUPPORT', 'TOURS', 'TOURS_CONCIERGE', 'HOTELS_AND_VILLAS']
const TOURS_CALLBACK_TYPES = ['TOURS', 'TOURS_CONCIERGE']
const CALLBACK_TYPES_WITH_NAME = ['FLIGHTS', 'LESA', 'SALES', 'SUPPORT', 'TOURS_CONCIERGE']
const CALLBACK_TYPES_WITH_EMAIL = ['LESA', 'SUPPORT', 'SALES', 'TOURS_CONCIERGE']

const messages = defineMessages({
  anytime: {
    id: 'D5x2F4IzxftPMWpB2Fa6',
    defaultMessage: 'Anytime',
    description: 'Default travel dates value for callback request form',
  },
  hotelsAndVillasIntro: {
    id: 'xij7p65cctxiIbO_l4QY',
    defaultMessage: 'Hi {givenName}, you are entitled to a personalised concierge call to assist with your booking. Share your contact details, and our 24/7 concierge team will get back to you as soon as possible.',
    description: 'Introduction text for hotels and villas callback request with personalized greeting',
  },
  callbackIntro: {
    id: '0Oq4gs-v2jhU8wE5bxS_',
    defaultMessage: 'Share your details with us, and our {teamType} team will get back to you as soon as possible.',
    description: 'Introduction text for callback request with team type',
  },
  invalidCharacters: {
    id: 'WjKkTpT4cLJJ8i0H0AB4',
    defaultMessage: 'Invalid characters. Please use only letters and spaces',
    description: 'Validation error for callback form fields with invalid characters',
  },
  firstNameLabel: {
    id: 'vn47O_iQED4cwD1Wt3o2',
    defaultMessage: 'First name *',
    description: 'Form field label for first name input in the callback form',
  },
  firstNamePlaceholder: {
    id: 'JUCDQ7Rz9HdXILLczwTI',
    defaultMessage: 'Your first name',
    description: 'Input placeholder text for first name input in the callback form',
  },
  firstNameRequired: {
    id: 'qzZDCdNdLn_SxWShwwrF',
    defaultMessage: 'First name is required',
    description: 'Required validation error for first name input on the form',
  },
  lastNameLabel: {
    id: 'fop5bbvh2FBNn0TgXeXR',
    defaultMessage: 'Last name *',
    description: 'Form field label for last name input in the callback form',
  },
  lastNamePlaceholder: {
    id: 'Ml_IXB99D3e5AQ9oNrQY',
    defaultMessage: 'Your last name',
    description: 'Input placeholder text for last name input in the callback form',
  },
  lastNameRequired: {
    id: 'zllXrM8MQFzGeYryQI1_',
    defaultMessage: 'Last name is required',
    description: 'Required validation error for last name input on the form',
  },
  emailInvalid: {
    id: 'opDchOAfkRQxGRCFQ7fy',
    defaultMessage: 'Invalid email address',
    description: 'Validation error for invalid email format in callback form',
  },
  emailLabel: {
    id: 'DOnRgbCi5Q8hci9ODjz2',
    defaultMessage: 'Email address *',
    description: 'Form field label for the email input in callback form',
  },
  emailPlaceholder: {
    id: 'cMULTOwGTGHNc8cj1Y0P',
    defaultMessage: 'Enter email address',
    description: 'Placeholder for email input in callback form when field is empty',
  },
  emailRequired: {
    id: 'RCjQqjdeTdYIAbDYDgbv',
    defaultMessage: 'Email address is required',
    description: 'Required validation error for email input when an issue occurs',
  },
  mobileNumberLabel: {
    id: 'go8BNgqVpuBrmLFRpj2q',
    defaultMessage: 'Mobile number',
    description: 'Label for mobile phone number input in callback form',
  },
  mobileNumberPlaceholder: {
    id: '0j1EJRqxK_ms_TE4U1-h',
    defaultMessage: 'e.g. 412 345 678',
    description: 'Placeholder example for mobile phone number input on the form',
  },
  mobileNumberRequired: {
    id: 'CsIQSKwulZJojENZf0Iz',
    defaultMessage: 'Mobile number is required',
    description: 'Required validation error for mobile number input on the form',
  },
  destinationLabel: {
    id: 'c-zLt9rk7BGZ6CLp2Kpw',
    defaultMessage: 'Where do you plan to go?',
    description: 'Form field label for the destination input in the callback form',
  },
  destinationPlaceholder: {
    id: 'YFn879RvRoMoKRZOqPTd',
    defaultMessage: 'The destination you would like to travel to',
    description: 'Placeholder text for the travel destination input in the agent callback request form',
  },
  destinationRequired: {
    id: 'dILRXjOqc9lwFtZjQH_T',
    defaultMessage: 'Destination is required',
    description: 'Required validation error for destination input on the form',
  },
  travelDatesLabel: {
    id: 'yNuHqxfL59_XrA-KHxMM',
    defaultMessage: 'When are you looking to travel?',
    description: 'Label for travel dates input in hotels or tours callback form',
  },
  travelDatesPlaceholder: {
    id: 'K6nVOu2V5IWSU6UFxhAw',
    defaultMessage: 'The dates you would like to travel on',
    description: 'Placeholder for travel dates input in hotels or tours callback form',
  },
  travelDatesRequired: {
    id: 'R6NBcBgGLqILGXaMHllV',
    defaultMessage: 'Dates are required',
    description: 'Required validation error for travel dates input on callback form',
  },
  numberOfTravellersLabel: {
    id: 'bHCcwn7NF1jmbos5WSHO',
    defaultMessage: 'How many people will you be travelling with?',
    description: 'Label for number of travellers input in callback form',
  },
  numberOfTravellersPlaceholder: {
    id: 'ussxbY0uMzArbDjXqica',
    defaultMessage: 'The number of people you will be travelling with',
    description: 'Placeholder for number of travellers input in the traveller form',
  },
  numberOfTravellersRequired: {
    id: 'C-AgyBqBNCBjB0lu4roK',
    defaultMessage: 'Number of people you are travelling with is required',
    description: 'Required validation error for number of travellers input',
  },
  supportMessageLabel: {
    id: 'H82x5vzgcR4zPUKHY0u-',
    defaultMessage: 'Message',
    description: 'Label for message textarea in support/sales callback form',
  },
  supportMessagePlaceholder: {
    id: 'TLD4qrB48tjfoRcAwKFP',
    defaultMessage: 'e.g. I have a payment issue',
    description: 'Placeholder for message textarea in support/sales callback form',
  },
  travelMessageLabel: {
    id: 'VDNrD6_RoHsgP4jjx9NC',
    defaultMessage: 'Any other questions or comments?',
    description: 'Label for message textarea in hotels/tours callback form',
  },
  travelMessagePlaceholder: {
    id: 'oaDvpbb7ue1PVnTouJ5n',
    defaultMessage: 'e.g. Do I need a vaccine?',
    description: 'Placeholder for message textarea in hotels/tours callback form',
  },
  flightsMessageLabel: {
    id: 'apNP6219d9bXmw9lXQqs',
    defaultMessage: 'What would you like to ask?',
    description: 'Label for message textarea in flights callback form',
  },
  flightsMessagePlaceholder: {
    id: 'JHP3lGCjdiEJy_26jxkB',
    defaultMessage: 'e.g. Can I change my booking date?',
    description: 'Placeholder for message textarea in flights callback form',
  },
})

function RequestAgentCallbackFields(props: Props) {
  const {
    type,
    requester,
    messagePlaceholder,
    filters,
    location,
    campaign,
    prefillFirstName,
    prefillLastName,
    prefillEmail,
    prefillPhone,
    prefillPhonePrefix,
    emailReadOnly = false,
    children,
  } = props

  const intl = useIntl()
  const loggedIn = requester.type === 'authenticated'
  const account = requester.type === 'authenticated' ? requester.account : undefined
  const [numberOfTravellers, setNumberOfTravellers] = useState(parseSelectedNumberOfTravellersToFormText(filters ?? {}, intl) ?? '')
  const [travelDates, setTravelDates] = useState(parseSelectedTravelDatesToFormText(filters ?? {}, intl) ?? intl.formatMessage(messages.anytime))

  useEffect(() => {
    if (filters) {
      setNumberOfTravellers(parseSelectedNumberOfTravellersToFormText(filters, intl) || '')
      setTravelDates(parseSelectedTravelDatesToFormText(filters, intl) || intl.formatMessage(messages.anytime))
    }
  }, [filters, intl])

  const shouldCollectName = !loggedIn || CALLBACK_TYPES_WITH_NAME.includes(type)
  const shouldCollectEmail = !loggedIn || CALLBACK_TYPES_WITH_EMAIL.includes(type)
  const shouldShowHotelsAndVillasIntro = type === 'HOTELS_AND_VILLAS' && loggedIn && !!account?.givenName
  const shouldShowGenericIntro = CALLBACK_TYPES_WITH_INTRO.includes(type) && !shouldShowHotelsAndVillasIntro
  const isToursCallback = TOURS_CALLBACK_TYPES.includes(type)
  const callbackTeamType = type === 'SALES'
    ? 'sales'
    : isToursCallback
      ? 'tours concierge'
      : type === 'HOTELS_AND_VILLAS'
        ? 'concierge'
        : 'support'

  const handleNumberOfTravellersChange: ChangeEventHandler<HTMLInputElement> = useCallback((e) => {
    setNumberOfTravellers(e.target.value)
  }, [])

  const handleTravelDatesChange: ChangeEventHandler<HTMLInputElement> = useCallback((e) => {
    setTravelDates(e.target.value)
  }, [])

  return (
    <VerticalSpacer gap={20}>
      {shouldShowHotelsAndVillasIntro && (
        <BodyTextBlock variant="large">
          {intl.formatMessage(
            messages.hotelsAndVillasIntro,
            { givenName: account.givenName },
          )}
        </BodyTextBlock>
      )}
      {shouldShowGenericIntro && (
        <BodyTextBlock variant="large">
          {intl.formatMessage(
            messages.callbackIntro,
            { teamType: callbackTeamType },
          )}
        </BodyTextBlock>
      )}
      <Group direction="vertical" gap={8}>
        {shouldCollectName && (
          <Group direction="horizontal" gap={8} horizontalAlign="stretch">
            <TextInput
              name="firstName"
              defaultValue={prefillFirstName ?? account?.givenName}
              invalidErrorMessage={intl.formatMessage(messages.invalidCharacters)}
              label={intl.formatMessage(messages.firstNameLabel)}
              maxLength={MAX_LENGTH}
              minLength={2}
              pattern="^[A-Za-z ]+$"
              placeholder={intl.formatMessage(messages.firstNamePlaceholder)}
              required
              requiredErrorMessage={intl.formatMessage(messages.firstNameRequired)}
            />
            <TextInput
              name="lastName"
              defaultValue={prefillLastName ?? account?.surname}
              invalidErrorMessage={intl.formatMessage(messages.invalidCharacters)}
              label={intl.formatMessage(messages.lastNameLabel)}
              maxLength={MAX_LENGTH}
              minLength={2}
              pattern="^[A-Za-z ]+$"
              placeholder={intl.formatMessage(messages.lastNamePlaceholder)}
              required
              requiredErrorMessage={intl.formatMessage(messages.lastNameRequired)}
            />
          </Group>
        )}
        {shouldCollectEmail && (
          <TextInput
            invalidErrorMessage={intl.formatMessage(messages.emailInvalid)}
            defaultValue={prefillEmail ?? account?.email}
            label={intl.formatMessage(messages.emailLabel)}
            maxLength={MAX_LENGTH}
            name="email"
            placeholder={intl.formatMessage(messages.emailPlaceholder)}
            readOnly={emailReadOnly}
            required
            requiredErrorMessage={intl.formatMessage(messages.emailRequired)}
            type="email"
          />
        )}
        <AgentCallbackRequestDateTimeInput type={type} />
        <PhoneInput
          defaultPhoneNumber={prefillPhone ?? account?.phone}
          defaultPhonePrefix={prefillPhonePrefix ?? account?.phonePrefix}
          label={intl.formatMessage(messages.mobileNumberLabel)}
          name="phone"
          placeholder={intl.formatMessage(messages.mobileNumberPlaceholder)}
          required
          requiredErrorMessage={intl.formatMessage(messages.mobileNumberRequired)}
        />
        {(type === 'HOTELS_AND_VILLAS' || type === 'TOURS') && (
          <>
            <TextInput
              name="destination"
              defaultValue={location}
              invalidErrorMessage={intl.formatMessage(messages.invalidCharacters)}
              label={intl.formatMessage(messages.destinationLabel)}
              maxLength={MAX_LENGTH}
              minLength={2}
              autoComplete="off"
              pattern="^[A-Za-z ]+$"
              placeholder={intl.formatMessage(messages.destinationPlaceholder)}
              required
              requiredErrorMessage={intl.formatMessage(messages.destinationRequired)}
            />
            {type === 'TOURS' &&
              <TextInput
                name="travelDates"
                label={intl.formatMessage(messages.travelDatesLabel)}
                maxLength={MAX_LENGTH}
                minLength={2}
                autoComplete="off"
                placeholder={intl.formatMessage(messages.travelDatesPlaceholder)}
                required
                requiredErrorMessage={intl.formatMessage(messages.travelDatesRequired)}
              />}
            {type === 'HOTELS_AND_VILLAS' &&
              <TextInput
                name="travelDates"
                value={travelDates}
                onChange={handleTravelDatesChange}
                label={intl.formatMessage(messages.travelDatesLabel)}
                maxLength={MAX_LENGTH}
                minLength={2}
                autoComplete="off"
                placeholder={intl.formatMessage(messages.travelDatesPlaceholder)}
                required
                startIcon={<LineCalendarIcon />}
                requiredErrorMessage={intl.formatMessage(messages.travelDatesRequired)}
              />}
            {type === 'HOTELS_AND_VILLAS' && (
              <TextInput
                name="numberOfTravellers"
                value={numberOfTravellers}
                onChange={handleNumberOfTravellersChange}
                label={intl.formatMessage(messages.numberOfTravellersLabel)}
                maxLength={MAX_LENGTH}
                minLength={1}
                autoComplete="off"
                placeholder={intl.formatMessage(messages.numberOfTravellersPlaceholder)}
                required
                requiredErrorMessage={intl.formatMessage(messages.numberOfTravellersRequired)}
              />
            )}
          </>
        )}
        {(type === 'LESA' || type === 'SUPPORT' || type === 'SALES' || type === 'TOURS_CONCIERGE') && (
          <TextArea
            label={intl.formatMessage(messages.supportMessageLabel)}
            maxLength={MAX_LENGTH_COMMENTS}
            name="message"
            placeholder={messagePlaceholder ?? intl.formatMessage(messages.supportMessagePlaceholder)}
          />
        )}
        {(type === 'HOTELS_AND_VILLAS' || type === 'TOURS') && (
          <TextArea
            label={intl.formatMessage(messages.travelMessageLabel)}
            maxLength={MAX_LENGTH_COMMENTS}
            name="message"
            placeholder={messagePlaceholder ?? intl.formatMessage(messages.travelMessagePlaceholder)}
          />
        )}
        {type === 'FLIGHTS' && (
          <TextArea
            label={intl.formatMessage(messages.flightsMessageLabel)}
            maxLength={MAX_LENGTH_COMMENTS}
            name="message"
            placeholder={messagePlaceholder ?? intl.formatMessage(messages.flightsMessagePlaceholder)}
          />
        )}
        <HiddenInput
          name="type"
          value={type}
        />
        {!!campaign && (
          <HiddenInput
            name="campaign"
            value={campaign}
          />
        )}
      </Group>
      {children}
    </VerticalSpacer>
  )
}

export default RequestAgentCallbackFields
